import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { C2Button } from '../components/c2';
import Sidebar from '../components/dashboard/Sidebar';
import Topbar from '../components/dashboard/Topbar';
import CreateLinkModal from '../components/dashboard/CreateLinkModal';
import UseExtensionModal from '../components/dashboard/UseExtensionModal';
import EmptyState from '../components/dashboard/EmptyState';
import { api, getToken, type Link as LinkRecord, type User } from '../lib/api';
import { waitForExtension, waitForSignInState } from '../lib/extensionPresence';
import { gtagOnce, identifyUser } from '../lib/analytics';
import {
  groupByAlias,
  ownerFor,
  relativeTime,
  stripScheme,
  type GroupedLink,
} from '../lib/dashboardHelpers';
import type { PastelColor } from '../components/c2/Pill';

const pastelBg: Record<PastelColor, string> = {
  peach: 'var(--color-peach)',
  mint: 'var(--color-mint)',
  sky: 'var(--color-sky)',
  lemon: 'var(--color-lemon)',
  lilac: 'var(--color-lilac)',
};
const pastelDeep: Record<PastelColor, string> = {
  peach: 'var(--color-peach-deep)',
  mint: 'var(--color-mint-deep)',
  sky: 'var(--color-sky-deep)',
  lemon: 'var(--color-lemon-deep)',
  lilac: 'var(--color-lilac-deep)',
};

type Scope = 'All' | 'Mine' | 'Team';
type Sort = 'updated' | 'clicks' | 'alpha';

function NoEditTooltip({ reason, children }: { reason: string; children: ReactNode }) {
  return (
    <span className="relative inline-flex group/edit" style={{ verticalAlign: 'middle' }}>
      {children}
      <span
        role="tooltip"
        className="absolute right-0 whitespace-normal font-medium opacity-0 group-hover/edit:opacity-100 transition-opacity pointer-events-none"
        style={{
          bottom: '100%',
          marginBottom: 8,
          background: 'var(--color-ink)',
          color: '#fff',
          padding: '8px 12px',
          borderRadius: 10,
          fontSize: 12,
          boxShadow: 'var(--shadow-md)',
          zIndex: 10,
          maxWidth: 240,
          width: 'max-content',
          lineHeight: 1.45,
        }}
      >
        {reason}
      </span>
    </span>
  );
}

interface StatProps { label: string; value: string }

function Stat({ label, value }: StatProps) {
  return (
    <div
      className="bg-surface"
      style={{
        padding: '10px 16px',
        border: '1px solid var(--color-hairline)',
        borderRadius: 14,
        minWidth: 120,
      }}
    >
      <div
        className="font-semibold uppercase mb-1"
        style={{ fontSize: 11, color: 'var(--color-ink-muted)', letterSpacing: '0.04em' }}
      >
        {label}
      </div>
      <div className="text-[22px] font-bold text-ink leading-tight tabular-nums" style={{ letterSpacing: '-0.01em' }}>
        {value}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [user, setUser] = useState<User | null>(null);
  const [allLinks, setAllLinks] = useState<LinkRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<Scope>('All');
  const [sort, setSort] = useState<Sort>('updated');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<LinkRecord | null>(null);
  const [useExtensionOpen, setUseExtensionOpen] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  // Auth gate: bounce to /login if no token
  useEffect(() => {
    if (!getToken()) {
      window.location.href = '/login';
    }
  }, []);

  // Load /auth/me + /links in parallel
  const refresh = async () => {
    try {
      const [me, links] = await Promise.all([api.me(), api.listLinks()]);
      // Onboarding gate: a user who hasn't finished the 5-step setup
      // should never see the dashboard. Bounce them now.
      if (me.onboarding_complete === false) {
        window.location.href = '/onboarding';
        return;
      }
      // Extension-presence gate: re-checked on every sign-in. The extension's
      // content script (v3.6+) sets per-browser attributes; older versions
      // only set the install marker.
      const installed = await waitForExtension(1500);
      if (!installed) {
        window.location.href = '/onboarding?fix=extension';
        return;
      }
      const signInState = await waitForSignInState(500);
      if (signInState === 'no') {
        // v3.6+ definitively tells us this browser has no token. Strict redirect.
        window.location.href = '/onboarding?fix=extension';
        return;
      }
      if (signInState === 'unknown') {
        // Old extension (≤v3.5) — fall back to the global extension_last_seen
        // signal so we don't trap users in a redirect loop while Chrome
        // auto-updates them. Drop this branch once v3.6+ is broadly rolled out.
        const everSignedInToExtension = me.extension_last_seen != null;
        if (!everSignedInToExtension) {
          window.location.href = '/onboarding?fix=extension';
          return;
        }
      }
      setUser(me);
      setAllLinks(links);
      setLoadError(null);

      // PostHog identify + Google Ads milestone events. gtagOnce is idempotent
      // per session, so re-running this on every refresh is fine.
      identifyUser(me);
      gtagOnce('tgl_dashboard_loaded');
      const hasExtensionMarker = document.documentElement.hasAttribute('data-tidygolinks-ext');
      const extRecentlyActive = me.extension_last_seen
        ? Date.now() - new Date(me.extension_last_seen + 'Z').getTime() < 24 * 60 * 60 * 1000
        : false;
      if (hasExtensionMarker) gtagOnce('tgl_extension_installed');
      if (hasExtensionMarker && extRecentlyActive) gtagOnce('tgl_extension_signed_in');
      if ((me.link_count ?? links.length) > 0) gtagOnce('tgl_first_link_created');
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401 || status === 403) {
        window.location.href = '/login';
        return;
      }
      setLoadError((err as Error).message || 'Could not load workspace');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  // ⌘N / Ctrl+N to open create modal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n' && !modalOpen && !useExtensionOpen) {
        e.preventDefault();
        setUseExtensionOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, useExtensionOpen]);

  const groupedLinks: GroupedLink[] = useMemo(() => groupByAlias(allLinks), [allLinks]);

  const filtered = useMemo(() => {
    let out = groupedLinks.slice();
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(
        l =>
          l.slug.toLowerCase().includes(q) ||
          (l.description ?? '').toLowerCase().includes(q) ||
          l.url.toLowerCase().includes(q) ||
          l.alternates.some(a => a.toLowerCase().includes(q)),
      );
    }
    if (scope === 'Mine' && user) {
      out = out.filter(l => l.created_by === user.id);
    }
    if (scope === 'Team' && user) {
      out = out.filter(l => l.created_by !== user.id);
    }
    if (sort === 'clicks') {
      out.sort((a, b) => b.click_count - a.click_count);
    } else if (sort === 'alpha') {
      out.sort((a, b) => a.slug.localeCompare(b.slug));
    } else {
      out.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }
    return out;
  }, [groupedLinks, search, scope, sort, user]);

  const totalClicks = useMemo(
    () => allLinks.reduce((acc, l) => acc + l.click_count, 0),
    [allLinks],
  );

  const onCopy = async (slug: string) => {
    try { await navigator.clipboard?.writeText(`go/${slug}`); } catch { /* ignore */ }
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 1200);
  };

  const onEdit = (link: LinkRecord) => {
    setEditing(link);
    setModalOpen(true);
  };

  const onDelete = async (slug: string) => {
    if (!window.confirm(`Delete go/${slug}? This can't be undone.`)) return;
    try {
      await api.deleteLink(slug);
      await refresh();
    } catch (err) {
      window.alert((err as Error).message || 'Delete failed');
    }
  };

  const onClaim = async (slug: string) => {
    try {
      await api.claimLink(slug);
      await refresh();
    } catch (err) {
      window.alert((err as Error).message || 'Could not claim link');
    }
  };

  const openCreate = () => {
    setUseExtensionOpen(true);
  };

  return (
    <div className="flex min-h-screen bg-bg font-body">
      <Sidebar user={user} />

      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar
          crumbs={[user?.email?.split('@')[1]?.split('.')[0] ?? 'Workspace', 'My links']}
          rightSlot={
            <C2Button variant="accent" size="sm" onClick={openCreate}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New go/ link
            </C2Button>
          }
        />

        <main className="flex-1">
          {loading ? (
            <div className="text-center text-ink-soft" style={{ padding: '80px 20px' }}>Loading…</div>
          ) : loadError ? (
            <div
              className="mx-auto text-center bg-surface"
              style={{
                margin: '60px auto 0',
                maxWidth: 480,
                padding: 32,
                border: '1px solid var(--color-hairline)',
                borderRadius: 20,
              }}
            >
              <h2 className="font-display font-bold text-ink m-0 mb-2" style={{ fontSize: 22 }}>
                Couldn't load your workspace
              </h2>
              <p className="text-[15px] text-ink-soft m-0 mb-5">{loadError}</p>
              <C2Button variant="outline" onClick={() => { setLoading(true); void refresh(); }}>Try again</C2Button>
            </div>
          ) : groupedLinks.length === 0 ? (
            <EmptyState onCreate={() => setUseExtensionOpen(true)} />
          ) : (
            <div>
              <div style={{ padding: '28px 32px 20px' }}>
                <div className="flex items-end justify-between gap-6 flex-wrap">
                  <div>
                    <h1
                      className="font-display font-bold text-ink m-0"
                      style={{ fontSize: 34, letterSpacing: '-0.02em', lineHeight: 1.1 }}
                    >
                      My links
                    </h1>
                    <p className="text-sm text-ink-soft mt-1.5 m-0">
                      Everything your team can reach via{' '}
                      <span className="font-mono font-semibold text-ink">go/</span> — searchable, clickable, yours to maintain.
                    </p>
                  </div>
                  <div className="flex gap-5">
                    <Stat label="Total clicks" value={totalClicks.toLocaleString()} />
                    <Stat label="Active links" value={String(groupedLinks.length)} />
                  </div>
                </div>
              </div>

              <div
                className="bg-surface overflow-hidden"
                style={{
                  margin: '0 32px 32px',
                  border: '1px solid var(--color-hairline)',
                  borderRadius: 20,
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                {/* Filter bar */}
                <div
                  className="flex items-center flex-wrap bg-surface"
                  style={{ gap: 10, padding: '14px 20px', borderBottom: '1px solid var(--color-hairline)' }}
                >
                  <div className="flex gap-1.5">
                    {(['All', 'Mine', 'Team'] as Scope[]).map(s => {
                      const active = scope === s;
                      return (
                        <button
                          key={s}
                          onClick={() => setScope(s)}
                          className="cursor-pointer transition-all duration-150"
                          style={{
                            padding: '5px 12px',
                            fontSize: 12.5,
                            fontWeight: active ? 600 : 500,
                            background: active ? 'var(--accent-soft)' : 'transparent',
                            color: active ? 'var(--accent-deep)' : 'var(--color-ink-soft)',
                            border: `1px solid ${active ? 'color-mix(in oklch, var(--accent-solid) 33%, transparent)' : 'var(--color-hairline-strong)'}`,
                            borderRadius: 999,
                          }}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex-1" />

                  <span className="text-xs text-ink-muted">
                    <b className="font-semibold text-ink-soft">{filtered.length}</b>{' '}
                    {filtered.length === 1 ? 'link' : 'links'}
                  </span>

                  <select
                    value={sort}
                    onChange={e => setSort(e.target.value as Sort)}
                    className="cursor-pointer outline-none"
                    style={{
                      padding: '6px 10px',
                      fontSize: 12.5,
                      fontWeight: 500,
                      background: 'var(--color-surface)',
                      color: 'var(--color-ink)',
                      border: '1px solid var(--color-hairline-strong)',
                      borderRadius: 8,
                      fontFamily: 'inherit',
                    }}
                  >
                    <option value="updated">Recently updated</option>
                    <option value="clicks">Most clicked</option>
                    <option value="alpha">Alphabetical</option>
                  </select>
                </div>

                {/* Table header */}
                <div
                  className="grid bg-bg-alt"
                  style={{
                    gridTemplateColumns: '240px 1fr 140px 110px 130px',
                    gap: 16,
                    padding: '10px 20px',
                    borderBottom: '1px solid var(--color-hairline)',
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--color-ink-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  <div>go/ link</div>
                  <div>Destination</div>
                  <div>Owner</div>
                  <div>Clicks</div>
                  <div>Updated</div>
                </div>

                {/* Rows */}
                {filtered.length === 0 ? (
                  <div
                    className="text-center text-ink-muted"
                    style={{ padding: '60px 20px' }}
                  >
                    <div className="text-[15px] font-medium mb-1.5">No links match your filters.</div>
                    <button
                      onClick={() => { setSearch(''); setScope('All'); }}
                      className="bg-transparent border-0 cursor-pointer font-semibold text-[13px]"
                      style={{ color: 'var(--accent-deep)' }}
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  filtered.map(link => {
                    const owner = ownerFor(link, user);
                    const canEdit = owner.isMine;
                    const noEditReason = owner.isOrphaned
                      ? 'Claim this link to take ownership and edit it.'
                      : `You can't edit this. Ask your teammate ${owner.email ?? '(unknown)'} to change it.`;
                    return (
                      <div
                        key={link.id}
                        className={`grid items-center transition-colors duration-150 group bg-surface ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
                        style={{
                          gridTemplateColumns: '240px 1fr 140px 110px 130px',
                          gap: 16,
                          padding: '14px 20px',
                          borderBottom: '1px solid var(--color-hairline)',
                        }}
                        onClick={() => { if (canEdit) onEdit(link); }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-bg)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-surface)'; }}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-mono font-semibold text-ink text-sm overflow-hidden text-ellipsis whitespace-nowrap">
                              <span className="text-ink-muted">go/</span>{link.slug}
                            </span>
                          </div>
                          {link.alternates.length > 0 && (
                            <div className="flex gap-1 mt-1.5 flex-nowrap overflow-hidden">
                              {link.alternates.slice(0, 2).map(a => (
                                <span
                                  key={a}
                                  title={`go/${a}`}
                                  className="font-mono font-medium text-ink-muted bg-bg-alt overflow-hidden text-ellipsis whitespace-nowrap"
                                  style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, maxWidth: 90 }}
                                >
                                  {a}
                                </span>
                              ))}
                              {link.alternates.length > 2 && (
                                <span
                                  className="text-ink-muted self-center font-medium"
                                  style={{ fontSize: 10 }}
                                >
                                  +{link.alternates.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="min-w-0">
                          {link.description && (
                            <div className="text-sm font-semibold text-ink overflow-hidden text-ellipsis whitespace-nowrap mb-0.5">
                              {link.description}
                            </div>
                          )}
                          <div className="text-xs text-ink-muted overflow-hidden text-ellipsis whitespace-nowrap font-mono">
                            {stripScheme(link.url)}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 min-w-0">
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0"
                            style={{
                              background: pastelBg[owner.color],
                              color: pastelDeep[owner.color],
                              fontSize: 10,
                            }}
                          >
                            {owner.initials}
                          </div>
                          <span className="text-xs text-ink-soft overflow-hidden text-ellipsis whitespace-nowrap">
                            {owner.name}
                          </span>
                        </div>

                        <div className="text-sm font-bold text-ink tabular-nums">
                          {link.click_count.toLocaleString()}
                        </div>

                        <div className="flex items-center justify-between gap-2 relative">
                          <span className="text-xs text-ink-muted whitespace-nowrap">
                            {relativeTime(link.created_at)}
                          </span>
                          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                            <button
                              onClick={e => { e.stopPropagation(); void onCopy(link.slug); }}
                              title="Copy go/ link"
                              className="w-7 h-7 inline-flex items-center justify-center bg-transparent border-0 cursor-pointer text-ink-soft transition-all duration-150"
                              style={{ borderRadius: 6 }}
                              onMouseEnter={e => {
                                e.currentTarget.style.background = 'var(--color-bg-alt)';
                                e.currentTarget.style.color = 'var(--color-ink)';
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = 'var(--color-ink-soft)';
                              }}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                            </button>
                            <button
                              onClick={e => { e.stopPropagation(); window.open(link.url, '_blank'); }}
                              title="Open destination"
                              className="w-7 h-7 inline-flex items-center justify-center bg-transparent border-0 cursor-pointer text-ink-soft transition-all duration-150"
                              style={{ borderRadius: 6 }}
                              onMouseEnter={e => {
                                e.currentTarget.style.background = 'var(--color-bg-alt)';
                                e.currentTarget.style.color = 'var(--color-ink)';
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = 'var(--color-ink-soft)';
                              }}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                              </svg>
                            </button>
                            {canEdit ? (
                              <button
                                onClick={e => { e.stopPropagation(); void onDelete(link.slug); }}
                                title="Delete"
                                className="w-7 h-7 inline-flex items-center justify-center bg-transparent border-0 cursor-pointer text-ink-soft transition-all duration-150"
                                style={{ borderRadius: 6 }}
                                onMouseEnter={e => {
                                  e.currentTarget.style.background = 'var(--color-peach)';
                                  e.currentTarget.style.color = 'var(--color-peach-deep)';
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.background = 'transparent';
                                  e.currentTarget.style.color = 'var(--color-ink-soft)';
                                }}
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                </svg>
                              </button>
                            ) : owner.isOrphaned ? (
                              <button
                                onClick={e => { e.stopPropagation(); void onClaim(link.slug); }}
                                title="Claim — take ownership of this orphaned link"
                                className="inline-flex items-center justify-center bg-transparent cursor-pointer transition-all duration-150 font-semibold"
                                style={{
                                  height: 28,
                                  padding: '0 10px',
                                  borderRadius: 6,
                                  fontSize: 12,
                                  color: 'var(--accent-deep)',
                                  border: '1px solid var(--accent-soft)',
                                  background: 'var(--accent-tint)',
                                }}
                                onMouseEnter={e => {
                                  e.currentTarget.style.background = 'var(--accent-soft)';
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.background = 'var(--accent-tint)';
                                }}
                              >
                                Claim
                              </button>
                            ) : (
                              <NoEditTooltip reason={noEditReason}>
                                <button
                                  onClick={e => e.stopPropagation()}
                                  disabled
                                  title=""
                                  aria-label="Edit (disabled)"
                                  className="w-7 h-7 inline-flex items-center justify-center bg-transparent border-0 text-ink-faint"
                                  style={{ borderRadius: 6, cursor: 'not-allowed' }}
                                >
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                </button>
                              </NoEditTooltip>
                            )}
                          </div>
                          {copiedSlug === link.slug && (
                            <span
                              className="absolute font-semibold"
                              style={{
                                marginLeft: -80,
                                marginTop: -36,
                                fontSize: 11,
                                color: 'var(--accent-deep)',
                                background: 'var(--accent-soft)',
                                padding: '3px 9px',
                                borderRadius: 999,
                              }}
                            >
                              Copied!
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      <CreateLinkModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSaved={() => { void refresh(); }}
        editing={editing}
        allLinks={allLinks}
      />

      <UseExtensionModal
        open={useExtensionOpen}
        onClose={() => setUseExtensionOpen(false)}
      />
    </div>
  );
}
